import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/atoms/Button/Button';
import { academyTextbookApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';

type TextbookDetail = {
  id: string | number;
  name?: string;
  type?: string;
  coverUrl?: string;
  description?: string;
  registrationDate?: string;
};

type UnitRow = {
  id: number;
  title?: string;
  code?: string;
  unitType?: string;
  unitCategory?: string;
  registrationDate?: string;
};

const TYPE_LABELS: Record<string, string> = {
  '파닉스데이': '파닉스데이',
  '스타터_파닉스': '스타터 파닉스',
  '스타터_뉘앙스': '스타터 뉘앙스',
  '스타터_스트럭처': '스타터 스트럭처',
  '에세이': '에세이',
  '통번역': '통번역',
  '리딩북': '리딩북',
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const AcademyTextbookDetail: React.FC = () => {
  const { textbookId } = useParams<{ textbookId: string }>();
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix();
  const [loading, setLoading] = useState(false);
  const [textbook, setTextbook] = useState<TextbookDetail | null>(null);
  const [units, setUnits] = useState<UnitRow[]>([]);

  const loadDetail = async () => {
    if (!textbookId) return;
    setLoading(true);
    try {
      const res = await academyTextbookApi.detail(textbookId);
      const data = res?.data || res;
      setTextbook(data?.data || data || null);

      const ures = await academyTextbookApi.listUnits(textbookId);
      const udata = ures?.data || ures;
      setUnits(udata?.data || udata || []);
    } catch (e) {
      console.error('교재 상세 로드 실패', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textbookId]);

  const goList = () => {
    const base = slugPrefix || '';
    navigate(`${base}/academy/textbook/list`);
  };

  const goPreview = (unitId: number) => {
    const base = slugPrefix || '';
    navigate(`${base}/academy/textbook/${textbookId}/unit/${unitId}/preview`);
  };

  return (
    <main className='flex flex-col flex-1 py-8 px-4 md:px-8'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='heading1'>교재 상세</h1>
          <p className='text-gray-500 text-sm'>유닛을 선택해 시험 형태로 미리보기 할 수 있습니다.</p>
        </div>
        <Button intent='secondary' onClick={goList}>목록으로</Button>
      </div>

      {loading && (
        <div className='p-6 bg-white rounded-xl border border-gray-100'>불러오는 중...</div>
      )}

      {!loading && !textbook && (
        <div className='p-6 bg-white rounded-xl border border-gray-100'>교재를 찾을 수 없습니다.</div>
      )}

      {!loading && textbook && (
        <>
          <section className='p-6 bg-white rounded-xl border border-gray-100 mb-6 flex flex-col md:flex-row gap-4'>
            <div>
              {textbook.coverUrl ? (
                <img src={textbook.coverUrl} alt={textbook.name} className='w-32 h-44 object-cover rounded-md border' />
              ) : (
                <div className='w-32 h-44 rounded-md bg-gray-100 border flex items-center justify-center text-xs text-gray-400'>No Image</div>
              )}
            </div>
            <div className='flex-1 space-y-2'>
              <div className='text-xl font-semibold text-gray-900'>{textbook.name || '-'}</div>
              <div className='text-sm text-gray-600'>{TYPE_LABELS[textbook.type || ''] || textbook.type || '-'}</div>
              <div className='text-sm text-gray-500'>등록일: {formatDate(textbook.registrationDate)}</div>
              {textbook.description && <p className='text-sm text-gray-600 whitespace-pre-line'>{textbook.description}</p>}
            </div>
          </section>

          <section className='p-6 bg-white rounded-xl border border-gray-100'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-lg font-semibold text-gray-900'>연결된 유닛</h2>
            </div>
            <div className='overflow-auto'>
              <table className='min-w-full text-sm'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 py-3 text-left font-semibold text-gray-700'>유닛명</th>
                    <th className='px-4 py-3 text-left font-semibold text-gray-700'>유형</th>
                    <th className='px-4 py-3 text-left font-semibold text-gray-700'>등록일</th>
                    <th className='px-4 py-3 text-left font-semibold text-gray-700 w-32'>미리보기</th>
                  </tr>
                </thead>
                <tbody>
                  {units.length === 0 && (
                    <tr>
                      <td colSpan={4} className='px-4 py-6 text-center text-gray-500'>등록된 유닛이 없습니다.</td>
                    </tr>
                  )}
                  {units.map((unit) => (
                    <tr key={unit.id} className='border-t border-gray-100'>
                      <td className='px-4 py-3 text-gray-900 font-medium'>{unit.title || '-'}</td>
                      <td className='px-4 py-3 text-gray-700'>{unit.unitCategory || unit.unitType || '-'}</td>
                      <td className='px-4 py-3 text-gray-500'>{formatDate(unit.registrationDate)}</td>
                      <td className='px-4 py-3 text-right w-32'>
                        <Button onClick={() => goPreview(unit.id)}>미리보기</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default AcademyTextbookDetail;
